/**
 * A neutral reference record for the research-discovery tools, and its writers.
 *
 * Citation chasing (OpenAlex), the EndNote XML converter and the NBIB converter all end
 * in the same place — a list of references written out as RIS, BibTeX or CSL JSON — so
 * each reads its own source into a `RefRecord` and shares the writers below.
 *
 * RIS follows the Reference Manager / EndNote "RIS Format Documentation": a six-character
 * tag, `TY` first and `ER` last, one author per `AU` line (surname first), `PY` the four
 * digit year, `DA` the full date as YYYY/MM/DD/, and CR LF line endings. BibTeX is
 * written by the site's own `cslToBibtex`, so key and escaping rules match the rest of
 * CitationEasy.
 */

import { cslToBibtex } from "@/lib/tool-kit/reference-tools/bibtex-write";

export interface RefName {
  family: string;
  given: string;
  /** An organisation or a name that cannot be split. Wins over family/given. */
  literal?: string;
}

export interface RefRecord {
  /** A RIS reference type: JOUR, BOOK, CHAP, CONF, THES, RPRT, UNPB, GEN… */
  type: string;
  authors: RefName[];
  editors: RefName[];
  title: string;
  /** Journal, book or proceedings title. */
  container: string;
  /** Journal abbreviation. */
  containerAbbrev: string;
  /** Series title. */
  series: string;
  /** "2nd ed." */
  edition: string;
  /** Kind of work: a thesis type such as "Doctoral dissertation". */
  genre: string;
  /** Four-digit year, or "". */
  year: string;
  /** YYYY, YYYY/MM or YYYY/MM/DD — as much of the date as is known. */
  date: string;
  volume: string;
  issue: string;
  startPage: string;
  endPage: string;
  publisher: string;
  place: string;
  issn: string;
  isbn: string;
  doi: string;
  url: string;
  abstract: string;
  language: string;
  keywords: string[];
  notes: string[];
  /** A database accession number such as a PMID. */
  accession: string;
  database: string;
}

export function emptyRecord(type = "GEN"): RefRecord {
  return {
    type,
    authors: [],
    editors: [],
    title: "",
    container: "",
    containerAbbrev: "",
    series: "",
    edition: "",
    genre: "",
    year: "",
    date: "",
    volume: "",
    issue: "",
    startPage: "",
    endPage: "",
    publisher: "",
    place: "",
    issn: "",
    isbn: "",
    doi: "",
    url: "",
    abstract: "",
    language: "",
    keywords: [],
    notes: [],
    accession: "",
    database: "",
  };
}

const RE_DOI_PREFIX = /^(?:https?:\/\/(?:dx\.)?doi\.org\/|doi:\s*)/i;
const RE_WHITESPACE = /\s+/g;
const RE_DATE = /^(\d{4})(?:[/-](\d{1,2}))?(?:[/-](\d{1,2}))?/;
const RE_PAGE_RANGE = /^\s*([^\s\-–—]+)\s*[-–—]\s*([^\s\-–—]+)\s*$/;

export function cleanDoi(raw: string): string {
  return raw.trim().replace(RE_DOI_PREFIX, "").toLowerCase();
}

/** Collapse runs of whitespace (and any line breaks) to single spaces. */
export function squash(value: string): string {
  return value.replace(RE_WHITESPACE, " ").trim();
}

/** "Smith, Jane" → {Smith, Jane}; "Jane Smith" → {Smith, Jane} (last word is the surname). */
export function nameFromText(raw: string): RefName {
  const value = squash(raw);
  const comma = value.indexOf(",");
  if (comma >= 0) {
    return {
      family: value.slice(0, comma).trim(),
      given: value.slice(comma + 1).trim(),
    };
  }
  const space = value.lastIndexOf(" ");
  if (space < 0) {
    return { family: value, given: "" };
  }
  return {
    family: value.slice(space + 1),
    given: value.slice(0, space).trim(),
  };
}

/** Normalised date parts from "2014", "2014/05", "2014-05-01", "2014 May 1"-style text. */
export function dateParts(date: string): number[] {
  const match = RE_DATE.exec(date.trim());
  if (!match) {
    return [];
  }
  return match
    .slice(1)
    .filter((part) => part !== undefined)
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0);
}

/** "123-130" → ["123", "130"]; "e1234" → ["e1234", ""]. */
export function splitPages(pages: string): [string, string] {
  const range = RE_PAGE_RANGE.exec(pages);
  if (range) {
    return [range[1], range[2]];
  }
  return [pages.trim(), ""];
}

/* ------------------------------------------------------------------ RIS */

const EOL = "\r\n";

function nameToRis(name: RefName): string {
  if (name.literal) {
    return name.literal;
  }
  return name.given ? `${name.family}, ${name.given}` : name.family;
}

function pushTag(lines: string[], tag: string, value: string) {
  const text = squash(value);
  if (text !== "") {
    lines.push(`${tag}  - ${text}`);
  }
}

/**
 * The RIS `DA` value for a normalised date: "2014/05/01/". A bare year is left to `PY`,
 * so only dates with a month or more are written.
 */
export function risDate(date: string): string {
  const parts = dateParts(date);
  if (parts.length < 2) {
    return "";
  }
  return `${parts.map((part, index) => String(part).padStart(index === 0 ? 4 : 2, "0")).join("/")}/`;
}

/** One record as RIS lines, closed with `ER  - `. */
export function recordToRis(record: RefRecord): string {
  const lines = [`TY  - ${record.type}`];
  for (const author of record.authors) {
    pushTag(lines, "AU", nameToRis(author));
  }
  for (const editor of record.editors) {
    pushTag(lines, "A2", nameToRis(editor));
  }
  pushTag(lines, "TI", record.title);
  pushTag(lines, "T2", record.container);
  pushTag(lines, "T3", record.series);
  pushTag(lines, "J2", record.containerAbbrev);
  pushTag(lines, "ET", record.edition);
  pushTag(lines, "M3", record.genre);
  pushTag(
    lines,
    "PY",
    record.year || (dateParts(record.date)[0]?.toString() ?? "")
  );
  pushTag(lines, "DA", risDate(record.date));
  pushTag(lines, "VL", record.volume);
  pushTag(lines, "IS", record.issue);
  pushTag(lines, "SP", record.startPage);
  pushTag(lines, "EP", record.endPage);
  pushTag(lines, "PB", record.publisher);
  pushTag(lines, "CY", record.place);
  pushTag(lines, "SN", record.issn || record.isbn);
  pushTag(lines, "DO", record.doi);
  pushTag(lines, "UR", record.url);
  pushTag(lines, "LA", record.language);
  pushTag(lines, "AN", record.accession);
  pushTag(lines, "DB", record.database);
  for (const keyword of record.keywords) {
    pushTag(lines, "KW", keyword);
  }
  pushTag(lines, "AB", record.abstract);
  for (const note of record.notes) {
    pushTag(lines, "N1", note);
  }
  lines.push("ER  - ");
  return lines.join(EOL);
}

/** A whole .ris file: records separated by a blank line, CR LF line endings. */
export function recordsToRis(records: readonly RefRecord[]): string {
  if (records.length === 0) {
    return "";
  }
  return `${records.map(recordToRis).join(`${EOL}${EOL}`)}${EOL}`;
}

/* ------------------------------------------------------------------ CSL */

const CSL_TYPES: Record<string, string> = {
  JOUR: "article-journal",
  EJOUR: "article-journal",
  MGZN: "article-magazine",
  NEWS: "article-newspaper",
  BOOK: "book",
  EBOOK: "book",
  EDBOOK: "book",
  CHAP: "chapter",
  ECHAP: "chapter",
  CONF: "paper-conference",
  CPAPER: "paper-conference",
  THES: "thesis",
  RPRT: "report",
  UNPB: "manuscript",
  DATA: "dataset",
  ELEC: "webpage",
  BLOG: "post-weblog",
  GEN: "document",
};

function cslName(name: RefName): Record<string, string> {
  if (name.literal) {
    return { literal: name.literal };
  }
  return name.given
    ? { family: name.family, given: name.given }
    : { family: name.family };
}

function cslDate(date: string): { "date-parts": number[][] } | undefined {
  const parts = dateParts(date);
  return parts.length === 0 ? undefined : { "date-parts": [parts] };
}

function some<T>(value: T[]): T[] | undefined {
  return value.length > 0 ? value : undefined;
}

function text(value: string): string | undefined {
  return value === "" ? undefined : value;
}

/** A CSL JSON item, with only the fields that have a value. */
export function recordToCsl(
  record: RefRecord,
  id: string
): Record<string, unknown> {
  const pages = record.endPage
    ? `${record.startPage}-${record.endPage}`
    : record.startPage;
  const item: Record<string, unknown> = {
    id,
    type: CSL_TYPES[record.type] ?? "document",
    title: squash(record.title),
    author: some(record.authors.map(cslName)),
    editor: some(record.editors.map(cslName)),
    "container-title": text(record.container),
    "container-title-short": text(record.containerAbbrev),
    "collection-title": text(record.series),
    edition: text(record.edition),
    genre: text(record.genre),
    issued: cslDate(record.date || record.year),
    volume: text(record.volume),
    issue: text(record.issue),
    page: text(pages),
    publisher: text(record.publisher),
    "publisher-place": text(record.place),
    ISSN: text(record.issn),
    ISBN: text(record.isbn),
    DOI: text(record.doi),
    URL: text(record.url),
    abstract: text(record.abstract),
    language: text(record.language),
    keyword: text(record.keywords.join(", ")),
    PMID: record.database === "PubMed" ? text(record.accession) : undefined,
  };
  return Object.fromEntries(
    Object.entries(item).filter(([, value]) => value !== undefined)
  );
}

export function recordsToCsl(
  records: readonly RefRecord[]
): Record<string, unknown>[] {
  return records.map((record, index) => recordToCsl(record, `ref${index + 1}`));
}

export function recordsToCslJson(records: readonly RefRecord[]): string {
  return `${JSON.stringify(recordsToCsl(records), null, 2)}\n`;
}

export function recordsToBibtex(records: readonly RefRecord[]): string {
  return cslToBibtex(recordsToCsl(records));
}
